import React, { useState } from "react";
import {
  Link,
  Globe,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";

export function OnboardingForm() {
  const [formData, setFormData] = useState({
    siteUrl: "",
    locationLink: "",
    brochureLink: "",
    custominfo: "",
    customerName: "",
  });
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [responseType, setResponseType] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    console.log("Form submitted:", formData);
    try {
      const response = await fetch("http://127.0.0.1:3448/start", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error("Failed to onboard bot");
      const result = await response.json();
      console.log("Onboarding result:", result);
      setResponseMessage("Bot onboarded successfully!");
      setResponseType("success");
      setFormData({
        siteUrl: "",
        locationLink: "",
        brochureLink: "",
        custominfo: "",
        customerName: "",
      });
    } catch (error) {
      console.error("Onboarding error:", error);
      setResponseMessage("Failed to onboard bot.");
      setResponseType("error");
    } finally {
      setIsSubmitting(false);
      setShowModal(true);
    }
  };

  const ResponseModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {responseType === "success" ? "Success!" : "Error"}
          </h3>
          <button
            onClick={() => setShowModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="flex items-center justify-center mb-6">
          <div
            className={`rounded-full p-3 ${
              responseType === "success" ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {responseType === "success" ? (
              <CheckCircle className="h-8 w-8 text-green-500" />
            ) : (
              <AlertCircle className="h-8 w-8 text-red-500" />
            )}
          </div>
        </div>
        <p className="text-center text-gray-600 mb-6">{responseMessage}</p>
        <button
          onClick={() => {
            setShowModal(false);
            if (responseType === "success") {
              navigate("/bots");
            }
          }}
          className="w-full bg-blue-600 text-white rounded-lg py-3 hover:bg-blue-700 transition-colors"
        >
          {responseType === "success" ? "View All Bots" : "Try Again"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bot Onboarding
              </h1>
            </div>
            <button
              onClick={() => navigate("/bots")}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowBack className="h-4 w-4 mr-2" />
              Return
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Bot Details</h2>
            <p className="mt-1 text-sm text-gray-500">
              Fill in the details below to onboard a new Bot.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Site URL</span>
                </div>
              </label>
              <input
                type="url"
                value={formData.siteUrl}
                onChange={(e) =>
                  setFormData({ ...formData, siteUrl: e.target.value })
                }
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="https://example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                <div className="flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  <span>Location Link</span>
                </div>
              </label>
              <input
                type="url"
                value={formData.locationLink}
                onChange={(e) =>
                  setFormData({ ...formData, locationLink: e.target.value })
                }
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="https://maps.google.com/..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>Brochure Link</span>
                </div>
              </label>
              <input
                type="url"
                value={formData.brochureLink}
                onChange={(e) =>
                  setFormData({ ...formData, brochureLink: e.target.value })
                }
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="https://example.com/brochure.pdf"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>Customer Name</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) =>
                  setFormData({ ...formData, customerName: e.target.value })
                }
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Site Visit"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>Additional Information</span>
                </div>
              </label>
              <input
                type="text"
                value={formData.custominfo}
                onChange={(e) =>
                  setFormData({ ...formData, custominfo: e.target.value })
                }
                className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 focus:outline-none transition-colors sm:text-sm"
                placeholder="Enter additional information"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </main>
      {showModal && <ResponseModal />}
    </div>
  );
}
